import * as Yup from "yup";
import formModel from "../components/ProjectWizardPages/formModel";
import { RegisterOptions } from "react-hook-form";
import { InventoryData } from "../types/inventoryData";

const {
  formField: {
    projectName,
    description,
    location,
    deviceType,
    skipLocalCoordinator,
    localCoordinatorEmail,
    confirmEmail,
  },
} = formModel;

Yup.addMethod(Yup.array, "unique", function (message, mapper = (a: any) => a) {
  return this.test("unique", message, function (list) {
    let shouldSkip = this.options?.context?.skipAssignments;
    if (shouldSkip) {
      return true;
    } else {
      return list?.length === new Set(list?.map(mapper)).size;
    }
  });
});

const createUserAccountsValidation = Yup.object().shape({
  numberOfAccounts: Yup.number()
    .typeError("Must be a number")
    .required("errors.required")
    .min(1, "1 is minimum")
    .max(50, "50 is maximum"),
});

const createWorkspaceValidation = Yup.object().shape({
  tenantName: Yup.string()
    .required("errors.required")
    .min(2, "errors.min2")
    .max(32, "Too long"),
  availableDeviceTypes: Yup.array().min(1, "errors.required"),
});

const translationScheme = (lang: string) => {
  return Yup.object().when([], (schema, context) => {
    const selectedLanguage = context.context.selectedLanguage;

    if (selectedLanguage === lang) {
      return Yup.object().shape({
        title: Yup.string()
          .required("errors.required")
          .min(2, "errors.min2")
          .max(32, "errors.max32"),
        body: Yup.string().max(100),
      });
    }

    return schema;
  });
};

const recommendationValidation = Yup.object().shape({
  selectedLanguage: Yup.string().required("errors.required"),

  tags: Yup.array()
    .of(Yup.string().required("errors.required"))
    .min(1, "errors.required")
    .max(20),

  thumbnail: Yup.mixed().when("recommendationId", {
    is: (s: string) => {
      if (!s) return true;
    },
    then: Yup.mixed().required("errors.required"),
    otherwise: Yup.mixed(),
  }),
  video: Yup.mixed().when("recommendationId", {
    is: (s: string) => {
      if (!s) return true;
    },
    then: Yup.mixed()
      .required("errors.required")
      .test(
        "fileOrYoutube",
        "Video must be a valid YouTube link or a file with .mp4, .avi, .mov extension",
        (value) => {
          if (!value) return true; // Allow empty value

          if (value instanceof File) {
            const validExtensions = [".mp4", ".avi", ".mov"];
            const fileName = value.name.toLowerCase();
            return validExtensions.some((ext) => fileName.endsWith(ext));
          }

          if (typeof value === "string") {
            const youtubeRegex =
              /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.?be)\/.+$/;
            return youtubeRegex.test(value);
          }

          return false;
        }
      ),
    otherwise: Yup.mixed(),
  }),
  translations: Yup.object().shape({
    en: translationScheme("en"),
    da: translationScheme("da"),
    de: translationScheme("de"),
    es: translationScheme("es"),
    fr: translationScheme("fr"),
    nb: translationScheme("nb"),

    // Repeat for other languages similarly...
  }),
});

const preparationValidation = Yup.object().shape({
  activities: Yup.array()
    .of(
      Yup.object({
        activity: Yup.string().required("errors.required"),
      })
    )
    .min(1, "errors.minActivities")
    .max(32) // TODO figure out how many
    //@ts-ignore
    .unique("errors.duplicate", (a: Activity) => a.activity),
});

const partnerWizardValidation = [
  Yup.object().shape({
    [projectName.name]: Yup.string()
      .min(2, "errors.min2")
      .max(64, "errors.max64")
      .required("errors.required"),
    [description.name]: Yup.string().max(100, "errors.max400"),
    [location.name]: Yup.string().nullable().required("errors.required"),
    [deviceType.name]: Yup.array().min(1, "errors.required"),
  }),
  Yup.object().shape({
    [skipLocalCoordinator.name]: Yup.boolean(),
    [localCoordinatorEmail.name]: Yup.string().when(
      skipLocalCoordinator.name,
      //@ts-ignore
      (canSkip) => {
        if (!canSkip)
          return Yup.string()
            .email("errors.invalid")
            .required("errors.required");
      }
    ),
    [confirmEmail.name]: Yup.string().when(
      skipLocalCoordinator.name,
      //@ts-ignore
      (canSkip) => {
        if (!canSkip)
          return Yup.string().test(
            "email-match",
            "errors.emailNoMatch",
            function (value) {
              return this.parent.localCoordinatorEmail === value;
            }
          );
      }
    ),
  }),
];

const locationRuleMaxLength = 60;
const noteRuleMaxLength = 100;
const serialRuleMaxLength = 12;

const locationRule:
  | Omit<
      RegisterOptions<InventoryData, "location">,
      "disabled" | "setValueAs" | "valueAsNumber" | "valueAsDate"
    >
  | undefined = {
  maxLength: {
    value: locationRuleMaxLength,
    message: "errors.maxStringLength",
  },
};

const noteRule:
  | Omit<
      RegisterOptions<InventoryData, "note">,
      "disabled" | "setValueAs" | "valueAsNumber" | "valueAsDate"
    >
  | undefined = {
  maxLength: { value: noteRuleMaxLength, message: "errors.maxStringLength" },
};
const serialRule:
  | Omit<
      RegisterOptions<InventoryData, "serial">,
      "disabled" | "setValueAs" | "valueAsNumber" | "valueAsDate"
    >
  | undefined = {
  pattern: { value: /^\d+$/, message: "inventory.onlyNumbers" },
  minLength: { value: serialRuleMaxLength, message: "errors.maxStringLength" },
  maxLength: { value: serialRuleMaxLength, message: "errors.maxStringLength" },
};

const inventoryRules = {
  locationRule,
  locationRuleMaxLength,
  noteRule,
  noteRuleMaxLength,
  serialRule,
  serialRuleMaxLength,
};

export {
  preparationValidation,
  createWorkspaceValidation,
  partnerWizardValidation,
  recommendationValidation,
  createUserAccountsValidation,
  inventoryRules,
};
